import { useCallback, useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTheme } from '@mui/material';
import debounce from 'lodash.debounce';
import TextInputField from 'components/TextInputField';
import { AssetUrls } from 'config/assets';
import './QuickSearchBar.css';

interface Props {
  placeholder?: string;
  onSearchChange: (value: string) => void;
  onSearch?: (searchValue?: string) => void;
  bgImageUrl?: string;
}

const QuickSearchBar = ({ 
  placeholder = 'Search for cards...', 
  onSearchChange,
  onSearch,
  bgImageUrl 
}: Props) => {
  const theme = useTheme();
  const [currentValue, setCurrentValue] = useState('');

  const debouncedSearch = useMemo(
    () =>
      debounce((value: string) => {
        onSearchChange(value);
      }, 300),
    [onSearchChange]
  );

  const handleChange = useCallback(
    (text: string) => {
      setCurrentValue(text);
      debouncedSearch(text);
    },
    [debouncedSearch]
  );

  const handleGoClick = () => {
    if (onSearch) {
      onSearch(currentValue);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' && onSearch) {
      event.preventDefault();
      // Pass the actual current value, not the debounced state
      onSearch(currentValue);
    }
  };

  return (
    <div 
      className={`quick-search-bar ${bgImageUrl ? 'with-background' : ''}`}
      style={{ backgroundImage: bgImageUrl ? `url(${bgImageUrl})` : 'none' }}
    >
      <div className="search-input-wrapper">
        <TextInputField
          leftComponent={
            <FontAwesomeIcon
              size="sm"
              icon={'magnifying-glass'}
              color={theme.colors.white}
            />
          }
          placeholder={placeholder}
          value={currentValue}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
        />
        <button className="quick-search-go-button" onClick={handleGoClick}>
          <img
            src={AssetUrls.search.goIcon}
            alt="Go"
            style={{ width: 12, height: 12, filter: 'brightness(0) invert(1)' }}
          />
          <span>Go</span>
        </button>
      </div>
    </div>
  );
};

export default QuickSearchBar;
